interface FullExamNavigatorProps {
  totalQuestions: number;
  currentIndex: number;
  userAnswers: (string | null)[];
  flagged: boolean[];
  onNavigate: (index: number) => void;
}

export default function FullExamNavigator({
  totalQuestions,
  currentIndex,
  userAnswers,
  flagged,
  onNavigate,
}: FullExamNavigatorProps) {
  const getQuestionStatus = (index: number) => {
    if (userAnswers[index]) return 'answered';
    if (flagged[index]) return 'flagged';
    return 'unanswered';
  };

  const getButtonClass = (index: number) => {
    const status = getQuestionStatus(index);
    const isActive = index === currentIndex;

    if (isActive) {
      if (status === 'answered') return 'bg-emerald-600 text-white border-emerald-600';
      if (status === 'flagged') return 'bg-amber-600 text-white border-amber-600';
      return 'bg-indigo-600 text-white border-indigo-600';
    }

    if (status === 'answered') return 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200';
    if (status === 'flagged') return 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200';
    return 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50';
  };

  const renderSection = (title: string, start: number, end: number) => (
    <div className="mb-6">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 px-1">
        {title}
      </div>
      <div className="grid grid-cols-6 gap-2">
        {Array.from({ length: end - start + 1 }, (_, i) => {
          const index = start + i;
          return (
            <button
              key={index}
              onClick={() => onNavigate(index)}
              className={`w-full h-9 rounded-md text-sm font-medium border transition-all ${getButtonClass(index)}`}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );

  const answeredCount = userAnswers.filter(a => a !== null).length;
  const flaggedCount = flagged.filter(f => f).length;

  return (
    <div className="w-80 bg-slate-50 border-l border-slate-200 p-6 overflow-y-auto">
      <div className="mb-6">
        <h3 className="text-base font-bold text-slate-800 mb-4">Question Navigator</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500"></div>
              <span className="text-slate-600">Answered</span>
            </div>
            <span className="font-semibold text-slate-800">{answeredCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-slate-600">Flagged</span>
            </div>
            <span className="font-semibold text-slate-800">{flaggedCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-white border-2 border-slate-300"></div>
              <span className="text-slate-600">Unanswered</span>
            </div>
            <span className="font-semibold text-slate-800">{totalQuestions - answeredCount}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-300 pt-6">
        {renderSection('Section 1: Reading & Writing', 0, 53)}
        {renderSection('Section 2: Math', 54, 97)}
      </div>
    </div>
  );
}